import { Link } from "react-router-dom";

const PrivacyPolicy = () => {
  return (
    <div data-testid="privacy-policy-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-primary/5 py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-secondary">
              Privacy Policy
            </h1>
            <p className="text-slate-600 mt-4">
              Last updated: December 2024
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-slate max-w-none">
            <p className="text-lg text-slate-600 mb-8">
              CartNexa ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our cleaning services.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-12 mb-4">
              1. Information We Collect
            </h2>
            <h3 className="font-heading text-xl font-bold text-secondary mt-8 mb-3">
              Personal Information
            </h3>
            <p className="text-slate-600 mb-4">
              We may collect personal information that you voluntarily provide to us when you:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
              <li>Fill out contact or quote request forms</li>
              <li>Schedule cleaning services</li>
              <li>Create an account on our website</li>
              <li>Subscribe to our newsletter</li>
              <li>Contact us via phone or email</li>
            </ul>
            <p className="text-slate-600 mb-4">
              This information may include:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
              <li>Name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Home or office address</li>
              <li>Payment information</li>
              <li>Service preferences</li>
            </ul>

            <h3 className="font-heading text-xl font-bold text-secondary mt-8 mb-3">
              Automatically Collected Information
            </h3>
            <p className="text-slate-600 mb-4">
              When you visit our website, we may automatically collect certain information, including:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Operating system</li>
              <li>Pages visited and time spent</li>
              <li>Referring website</li>
              <li>Device information</li>
            </ul>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-12 mb-4">
              2. How We Use Your Information
            </h2>
            <p className="text-slate-600 mb-4">
              We use the information we collect to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
              <li>Provide, maintain, and improve our cleaning services</li>
              <li>Process and manage service bookings</li>
              <li>Send appointment confirmations and reminders</li>
              <li>Respond to your inquiries and provide customer support</li>
              <li>Send promotional communications (with your consent)</li>
              <li>Analyze website usage to improve user experience</li>
              <li>Comply with legal obligations</li>
              <li>Prevent fraudulent activity</li>
            </ul>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-12 mb-4">
              3. Cookie Usage
            </h2>
            <p className="text-slate-600 mb-4">
              We use cookies and similar tracking technologies to enhance your experience on our website. Cookies are small data files stored on your device that help us:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
              <li>Remember your preferences</li>
              <li>Understand how you use our website</li>
              <li>Improve website functionality</li>
              <li>Provide targeted advertising</li>
            </ul>
            <p className="text-slate-600 mb-4">
              You can control cookie settings through your browser. However, disabling cookies may limit some website functionality.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-12 mb-4">
              4. Information Sharing and Disclosure
            </h2>
            <p className="text-slate-600 mb-4">
              We do not sell, trade, or rent your personal information to third parties. We may share your information with:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
              <li><strong>Service Providers:</strong> Third-party vendors who help us operate our business (payment processors, scheduling software, etc.)</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-12 mb-4">
              5. Data Protection Measures
            </h2>
            <p className="text-slate-600 mb-4">
              We implement appropriate technical and organizational security measures to protect your personal information, including:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
              <li>SSL encryption for data transmission</li>
              <li>Secure data storage systems</li>
              <li>Access controls and authentication</li>
              <li>Regular security assessments</li>
              <li>Employee training on data protection</li>
            </ul>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-12 mb-4">
              6. Third-Party Tools and Services
            </h2>
            <p className="text-slate-600 mb-4">
              Our website may use third-party tools and services, including:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
              <li>Google Analytics for website analytics</li>
              <li>Google Maps for location services</li>
              <li>Social media plugins</li>
              <li>Payment processing services</li>
            </ul>
            <p className="text-slate-600 mb-4">
              These third parties have their own privacy policies governing the use of your information.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-12 mb-4">
              7. Your Rights
            </h2>
            <p className="text-slate-600 mb-4">
              Depending on your location, you may have the right to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
              <li>Access your personal information</li>
              <li>Correct inaccurate information</li>
              <li>Delete your personal information</li>
              <li>Opt out of marketing communications</li>
              <li>Object to certain processing activities</li>
            </ul>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-12 mb-4">
              8. Children's Privacy
            </h2>
            <p className="text-slate-600 mb-6">
              Our services are not intended for individuals under 18 years of age. We do not knowingly collect personal information from children.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-12 mb-4">
              9. Changes to This Privacy Policy
            </h2>
            <p className="text-slate-600 mb-6">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-12 mb-4">
              10. Contact Us
            </h2>
            <p className="text-slate-600 mb-4">
              If you have any questions or concerns about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-slate-50 rounded-xl p-6 mb-6">
              <p className="text-slate-700 font-medium">CartNexa</p>
              <p className="text-slate-600">783 Meadowbrook Lane</p>
              <p className="text-slate-600">Atlanta, GA 30303</p>
              <p className="text-slate-600 mt-2">
                Email: <a href="mailto:support@cartnexa.store" className="text-primary hover:underline">support@cartnexa.store</a>
              </p>
              <p className="text-slate-600">
                Phone: <a href="tel:+18717871311" className="text-primary hover:underline">+1-871-7871311</a>
              </p>
            </div>

            <div className="mt-12 pt-8 border-t border-slate-200">
              <Link to="/" className="text-primary font-medium hover:underline">
                ← Back to Home
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
